import React, { useEffect, useState } from "react";
import { BookText, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDateTime } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge } from "@/components/common";

export default function Cashbook() {
  const { branch, can, isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState({ in: [], out: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/cash-entries", branch ? { branch_id: branch.id } : {}),
        api.get("/cash-entries/categories").catch(() => ({ in: [], out: [] })),
      ]);
      setRows(r); setCats(c);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branch]);

  const totalIn = rows.filter((r) => r.type === "in").reduce((s, r) => s + r.amount, 0);
  const totalOut = rows.filter((r) => r.type === "out").reduce((s, r) => s + r.amount, 0);

  const del = async (r) => {
    if (!window.confirm(`Hapus entri "${r.category_label}" ${rupiah(r.amount)}?\n\nAngka di laporan (mis. Biaya Operasional) akan ikut menyesuaikan.`)) return;
    try { await api.del(`/cash-entries/${r.id}`); toast.success("Entri kas dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Buku Kas" subtitle={branch?.name} icon={BookText}
        actions={can("cash", "create") && <Button onClick={() => setAdding(true)} data-testid="add-cash-btn"><Plus size={16} /> Catat Kas</Button>} />
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4"><div className="text-xs text-slate-400">Masuk</div><div className="text-xl font-bold text-emerald-600">{rupiah(totalIn)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-400">Keluar</div><div className="text-xl font-bold text-rose-600">{rupiah(totalOut)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-400">Saldo</div><div className="text-xl font-bold text-slate-800">{rupiah(totalIn - totalOut)}</div></Card>
      </div>

      {loading ? <Loading /> : !rows.length ? <EmptyState icon={BookText} title="Belum ada transaksi kas hari ini" /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 font-medium">Waktu</th><th className="py-3 px-4 font-medium">Kategori</th><th className="py-3 px-4 font-medium">Catatan</th><th className="py-3 px-4 font-medium text-right">Jumlah</th>{isOwner && <th className="py-3 px-4"></th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50" data-testid={`cash-row-${r.id}`}>
                  <td className="py-3 px-4 text-slate-400 text-xs">{fmtDateTime(r.created_at)}</td>
                  <td className="py-3 px-4"><Badge className={r.type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{r.category_label}</Badge></td>
                  <td className="py-3 px-4 text-slate-500">{r.note || "-"}</td>
                  <td className={`py-3 px-4 text-right font-semibold ${r.type === "in" ? "text-emerald-600" : "text-rose-600"}`}>{r.type === "in" ? "+" : "-"} {rupiah(r.amount)}</td>
                  {isOwner && <td className="py-3 px-4 text-right"><button onClick={() => del(r)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-cash-${r.id}`} title="Hapus"><Trash2 size={15} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {adding && <CashModal cats={cats} branch={branch} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </>
  );
}

function CashModal({ cats, branch, onClose, onSaved }) {
  const [type, setType] = useState("out");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const list = type === "in" ? cats.in : cats.out;
  useEffect(() => { setCategory(list[0]?.key || ""); }, [type]); // eslint-disable-line

  const save = async () => {
    if (!branch) return toast.error("Pilih cabang");
    if (!category || !Number(amount)) return toast.error("Lengkapi kategori & jumlah");
    setBusy(true);
    try { await api.post("/cash-entries", { branch_id: branch.id, type, category, amount: Number(amount), method: "cash", note }); toast.success("Kas dicatat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Catat Kas" maxWidth="max-w-sm">
      <div className="space-y-3">
        <div className="flex rounded-xl border border-slate-300 overflow-hidden">
          {[["out", "Kas Keluar"], ["in", "Kas Masuk"]].map(([v, l]) => <button key={v} onClick={() => setType(v)} className={`flex-1 h-10 text-sm font-medium ${type === v ? "bg-primary text-white" : "text-slate-600"}`}>{l}</button>)}
        </div>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Kategori</span><select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="cash-cat">{list.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select></label>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jumlah</span><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="cash-amount" /></label>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Catatan</span><input value={note} onChange={(e) => setNote(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" /></label>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="cash-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
    </Modal>
  );
}
